import React, { useState, useMemo } from 'react';
import { Printer, Bluetooth, Eye, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  printEntregaReceipt,
  previewEntregaReceipt
} from '../../../utils/thermalPrinter';
import { getPrinterInstance } from '../../../utils/bluetoothPrinter';
import { getEntregaById } from '../services/deposito.services';

const PrintEntregaReceiptButton = ({ entrega, entregaData, disabled = false }) => {
  // Usar 'entrega' si viene, sino 'entregaData' para compatibilidad
  const data = entrega || entregaData;
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState('');

  // Instancia compartida de la impresora Bluetooth
  const bluetoothPrinter = useMemo(() => getPrinterInstance(), []);

  const getEntregaCompleta = async (entregaBasica) => {
    // Si ya tiene prendas, usarla directamente
    if (entregaBasica.prendas && entregaBasica.prendas.length > 0) {
      return entregaBasica;
    }
    
    // Si no, obtener detalles completos por ID
    try {
      const response = await getEntregaById(entregaBasica._id);
      return response.data || entregaBasica;
    } catch (error) {
      console.error('Error al obtener detalles completos:', error);
      return entregaBasica;
    }
  };

  const handlePrintThermal = async () => {
    if (!data) {
      console.error('No hay datos de entrega para imprimir');
      return;
    }

    setLoading(true);
    setCurrentAction('thermal');
    try {
      const entregaCompleta = await getEntregaCompleta(data);
      await printEntregaReceipt(entregaCompleta);
    } catch (error) {
      console.error('Error al imprimir recibo térmico:', error);
    } finally {
      setLoading(false);
      setCurrentAction('');
    }
  };

  const handlePrintBluetooth = async () => {
    if (!data) {
      console.error('No hay datos de entrega para imprimir');
      return;
    }

    setLoading(true);
    setCurrentAction('bluetooth');
    try {
      const entregaCompleta = await getEntregaCompleta(data);
      if (!bluetoothPrinter.isConnected()) {
        await bluetoothPrinter.connect();
      }
      await bluetoothPrinter.printEntregaReceipt(entregaCompleta);
    } catch (error) {
      console.error('Error al imprimir por Bluetooth:', error);
    } finally {
      setLoading(false);
      setCurrentAction('');
    }
  };

  const handlePreview = async () => {
    if (!data) {
      console.error('No hay datos de entrega para previsualizar');
      return;
    }

    setCurrentAction('preview');
    try {
      const entregaCompleta = await getEntregaCompleta(data);
      previewEntregaReceipt(entregaCompleta);
    } catch (error) {
      console.error('Error al generar vista previa:', error);
    } finally {
      setCurrentAction('');
    }
  };

  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={handlePrintThermal}
        disabled={disabled || loading}
        title="Imprimir en impresora térmica"
        className="text-muted-foreground hover:text-foreground"
      >
        {currentAction === 'thermal' && loading
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <Printer className="h-4 w-4" />}
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={handlePrintBluetooth}
        disabled={disabled || loading}
        title="Imprimir vía Bluetooth"
        className="text-muted-foreground hover:text-foreground"
      >
        {currentAction === 'bluetooth' && loading
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <Bluetooth className="h-4 w-4" />}
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={handlePreview}
        disabled={disabled || loading}
        title="Vista previa"
        className="text-muted-foreground hover:text-foreground"
      >
        <Eye className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default PrintEntregaReceiptButton;